import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";
import { motion } from "framer-motion";

export default function BlogPage() {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data || [])).catch(() => {});
  }, []);

  const dateFmt = (s) => new Date(s).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "2-digit", month: "long", year: "numeric" });

  const SITE = getSiteOrigin();
  const schema = posts.length ? [{
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE}/journal#blog`,
    name: lang === "fr" ? "Journal Phaneroo Prod" : "Phaneroo Prod Journal",
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    publisher: { "@id": `${SITE}/#studio` },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: lang === "fr" ? p.title_fr : p.title,
      url: `${SITE}/journal/${p.slug}`,
      datePublished: p.date,
      image: p.cover?.startsWith("http") ? p.cover : `${SITE}${p.cover}`,
    })),
  }, {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE}/journal` },
    ],
  }] : null;

  return (
    <main className="pt-40 lg:pt-48 pb-32 bg-[#050505] min-h-screen" data-testid="page-blog">
      <SEO
        title={lang === "fr" ? "Journal — Notes du studio" : "Journal — Notes from the studio"}
        description={lang === "fr"
          ? "Articles, conseils et histoires du studio Phaneroo Prod sur la production vidéo, la photographie et la création de contenu."
          : "Articles, advice and stories from Phaneroo Prod studio on video production, photography and content creation."}
        path="/journal"
        lang={lang}
        schema={schema}
      />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
            <span className="block w-8 h-px bg-rouge" /><span>{t.blog.eyebrow}</span>
          </div>
          <h1 className="mt-6 font-serif-editorial text-5xl sm:text-6xl lg:text-7xl text-ink-100 tracking-tighter leading-[1.02]">
            {t.blog.title}
          </h1>
        </Reveal>

        <div className="mt-20 border-t border-hair">
          {posts.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="border-b border-hair"
              data-testid={`blog-row-${i}`}
            >
              <Link
                to={`/journal/${p.slug}`}
                data-cursor={t.blog.read}
                className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-8 lg:py-10 items-center group"
              >
                <div className="lg:col-span-2 text-[11px] tracking-[0.22em] uppercase text-ink-500">{dateFmt(p.date)}</div>
                <div className="lg:col-span-6">
                  <h2 className="font-serif-editorial text-2xl lg:text-3xl text-ink-100 group-hover:text-rouge transition-colors leading-tight">
                    {lang === "fr" ? p.title_fr : p.title}
                  </h2>
                  <p className="mt-2 text-ink-300 text-sm max-w-2xl">{lang === "fr" ? p.excerpt_fr : p.excerpt}</p>
                </div>
                <div className="lg:col-span-2 text-[11px] tracking-[0.22em] uppercase text-ink-500">
                  {p.reading_time} {t.blog.min}
                </div>
                <div className="lg:col-span-2 aspect-[4/3] overflow-hidden bg-surface relative">
                  <img src={p.cover} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover saturate-50 group-hover:saturate-100 transition-all duration-700 group-hover:scale-105" />
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </main>
  );
}
